/**
 * useUnmaskChallenge — orchestrates the OTP flow that reveals masked data.
 *
 *   idle → (start)      POST /unmask/init   → awaiting-otp
 *   awaiting-otp → (verify) POST /unmask/verify → applyUnmaskToken → idle
 *
 * Mirrors features/profile/useOtpProtectedProfileUpdate. The token swap +
 * refetch + auto-revert timer all live in the unmask context; this hook only
 * drives the dialog and forwards the verified token.
 */
import * as React from 'react';
import { toast } from '@finmin/ui';
import { isApiError } from '@finmin/api-client';
import { useInitUnmask, useVerifyUnmask } from '@/api/unmask';
import { useUnmask } from './unmask-context';

type Phase = 'idle' | 'requesting' | 'awaiting-otp' | 'verifying';

function messageFromError(err: unknown, fallback: string): string {
  if (isApiError(err) && err.message) return err.message;
  return fallback;
}

export function useUnmaskChallenge() {
  const { applyUnmaskToken, isDashboardUnmasked } = useUnmask();
  const [phase, setPhase] = React.useState<Phase>('idle');
  const [otpExpiresInSec, setOtpExpiresInSec] = React.useState(0);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const initMutation = useInitUnmask();
  const verifyMutation = useVerifyUnmask();

  /** Request an OTP and open the dialog. */
  const start = React.useCallback(async () => {
    setErrorMessage(null);
    setPhase('requesting');
    try {
      const { exp } = await initMutation.mutateAsync();
      setOtpExpiresInSec(exp);
      setPhase('awaiting-otp');
    } catch (err) {
      setPhase('idle');
      toast.error('Could not send code', messageFromError(err, 'Please try again in a moment.'));
    }
  }, [initMutation]);

  /** Verify the entered OTP; on success swap the token + refetch revealed data. */
  const verify = React.useCallback(
    async (otp: string) => {
      setErrorMessage(null);
      setPhase('verifying');
      try {
        const { access_token, expires_in } = await verifyMutation.mutateAsync({ otp });
        applyUnmaskToken(access_token, expires_in);
        setPhase('idle');
        toast.success(
          'Details revealed',
          `Visible for ${Math.max(1, Math.round(expires_in / 60))} min, then hidden again.`,
        );
      } catch (err) {
        setErrorMessage(messageFromError(err, "That code didn't verify. Try again."));
        setPhase('awaiting-otp');
      }
    },
    [verifyMutation, applyUnmaskToken],
  );

  const cancel = React.useCallback(() => {
    setPhase('idle');
    setErrorMessage(null);
    initMutation.reset();
    verifyMutation.reset();
  }, [initMutation, verifyMutation]);

  return {
    /** Dialog is mounted while awaiting or verifying the OTP. */
    isOpen: phase === 'awaiting-otp' || phase === 'verifying',
    /** OTP request in flight (button spinner). */
    isStarting: phase === 'requesting',
    /** Verify in flight (dialog spinner). */
    isVerifying: phase === 'verifying',
    otpExpiresInSec,
    errorMessage,
    isDashboardUnmasked,
    start,
    verify,
    cancel,
  };
}
